import {
  PUBLIC_SCHEDULE_ENDPOINTS,
  PUBLIC_STANDINGS_ENDPOINTS,
  API_CONFIG,
  usesLocalFixtures,
} from '../../constants/api.constants';

/**
 * Constructs the public schedule URL based on environment
 *
 * Environment-aware: Returns the local fixture path in development, the
 * published path everywhere else (and in development under
 * `REACT_APP_USE_STAGING_DATA`)
 * Cache-busting: Adds timestamp query parameter to prevent stale data
 *
 * @param key - Schedule key (e.g., 'academy-division-26')
 * @returns Full URL with cache busting parameter
 */
export const buildPublicScheduleUrl = (key: string): string => {
  // Build environment-specific endpoint
  const endpoint = usesLocalFixtures()
    ? PUBLIC_SCHEDULE_ENDPOINTS.DEVELOPMENT(key)
    : PUBLIC_SCHEDULE_ENDPOINTS.PRODUCTION(key);

  // Add cache busting query parameter
  return addCacheBuster(endpoint);
};

/**
 * Constructs the public standings URL based on environment
 *
 * Environment-aware: Returns the local fixture path in development, the
 * published path everywhere else (and in development under
 * `REACT_APP_USE_STAGING_DATA`)
 * Cache-busting: Adds timestamp query parameter to prevent stale data
 *
 * @param key - Season key slug (e.g., 'academy-division-26')
 * @returns Full URL with cache busting parameter
 */
export const buildPublicStandingsUrl = (key: string): string => {
  // Build environment-specific endpoint
  const endpoint = usesLocalFixtures()
    ? PUBLIC_STANDINGS_ENDPOINTS.DEVELOPMENT(key)
    : PUBLIC_STANDINGS_ENDPOINTS.PRODUCTION(key);

  // Add cache busting query parameter
  return addCacheBuster(endpoint);
};

/**
 * Adds cache busting query parameter to URL
 *
 * Appends a timestamp query parameter to force browsers and CDNs
 * to fetch fresh data instead of serving cached responses
 *
 * @param url - Base URL
 * @returns URL with timestamp query parameter if cache busting is enabled
 */
export const addCacheBuster = (url: string): string => {
  if (!API_CONFIG.ENABLE_CACHE_BUSTING) {
    return url;
  }

  // Use correct separator based on whether URL already has query params
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}_t=${Date.now()}`;
};
